/* Copyright (c) 2024 MOCA Systems, Inc.  All rights reserved. */

import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	inject,
	Input,
	OnDestroy,
	Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MultiPanelService } from '../multi-panel.service';
import { MatIconModule } from '@angular/material/icon';
import { MultiPanelItemDirective } from '../multi-panel-item/multi-panel-item.directive';
import { Observable, Subject, takeUntil } from 'rxjs';

@Component({
	selector: 'tp-multi-panel-header',
	standalone: true,
	imports: [CommonModule, MatIconModule],
	templateUrl: './multi-panel-header.component.html',
	styleUrls: ['./multi-panel-header.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiPanelHeaderComponent implements OnDestroy {
	private multiPanelService = inject(MultiPanelService);
	private multiPanelItem = inject(MultiPanelItemDirective);

	// Adding this input in the header might not be the most elegant solution,
	// but since the header is the only part that isn't a directive and also contains the output,
	// this seems like the best place for now.
	// Ideally, the multi-panel header should have a wrapper component in the future.
	@Input() set collapsed(collapsed: boolean) {
		if (typeof collapsed === 'boolean') {
			this.multiPanelService.collapse(this.multiPanelItem.id, collapsed);
		}
	}
	@Output() readonly isCollapsed = new EventEmitter<boolean>();

	protected collapsed$!: Observable<boolean>;
	private shutdownSubject$: Subject<void> = new Subject();

	public ngOnInit() {
		this.collapsed$ = this.multiPanelService.isCollapsed$(this.multiPanelItem.id);
		this.collapsed$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((collapsed) => this.isCollapsed.emit(collapsed));
	}

	public toggleCollapse(): void {
		this.multiPanelService.toggleCollapse(this.multiPanelItem.id);
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}
}
